/**
 * Centralized styles for ForecastRecord component
 * Reduces duplication and ensures consistency
 */

import { SxProps, Theme } from '@mui/material';

// Typography variants
export const TYPOGRAPHY = {
  tiny: '10px',
  small: '12px',
} as const;

// Color utilities (matching existing ForecastRecord logic)
export const getAccuracyColor = (accuracy: number | null): string => {
  if (accuracy === null || Number.isNaN(accuracy)) return '#9ca3af'; // gray-400
  if (accuracy >= 80) return '#22c55e'; // green-500
  if (accuracy >= 60) return '#facc15'; // yellow-400
  return '#ef4444'; // red-500
};

export const getSignalColor = (signal: string): string => {
  switch (signal) {
    case 'STRONG_BUY':
      return '#4ade80'; // green-400
    case 'OPPORTUNITY':
      return '#60a5fa'; // blue-400
    case 'STRONG_SELL':
      return '#f87171'; // red-400
    case 'BULL_TRAP':
      return '#fbbf24'; // yellow-400
    default:
      return '#9ca3af'; // gray-400
  }
};

// Common SX styles
export const tableCellTiny: SxProps<Theme> = {
  fontSize: TYPOGRAPHY.tiny,
  color: 'text.secondary',
};

export const tableCellTinyPrimary: SxProps<Theme> = {
  fontSize: TYPOGRAPHY.tiny,
  fontWeight: 600,
  color: 'text.primary',
};

export const tableCellTinyBold: SxProps<Theme> = {
  fontSize: TYPOGRAPHY.tiny,
  fontWeight: 700,
};

export const clickableRow: SxProps<Theme> = {
  '&:hover': { bgcolor: 'action.hover' },
  cursor: 'pointer',
};

export const chipTiny: SxProps<Theme> = {
  height: 'auto',
  fontSize: TYPOGRAPHY.tiny,
  fontWeight: 700,
  py: 0.25,
  px: 0.5,
};

// Format helpers (matching existing ForecastRecord logic)
export const formatPercent = (value: number | null): string => {
  if (value === null || Number.isNaN(value)) return 'N/A';
  return `${value.toFixed(1)}%`;
};

export const formatPercentWithColor = (
  value: number | null
): { text: string; color: string } => {
  if (value === null || Number.isNaN(value)) {
    return { text: 'N/A', color: '#9ca3af' }; // gray-400
  }
  const text = `${value.toFixed(1)}%`;
  const color = value > 0 ? '#22c55e' : value < 0 ? '#ef4444' : '#d1d5db'; // green-500 : red-500 : gray-300
  return { text, color };
};
